import {
  AlertOptions, ConfirmOptions, ConfirmResult, Dialog, PromptOptions, PromptResult,
} from '@capacitor/dialog';
import { Capacitor } from '@capacitor/core';
import { Injectable, inject } from '@angular/core';

import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private alertController = inject(AlertController);
  private translate = inject(TranslateService);

  public async showAlert(config: AlertOptions): Promise<void> {
    config.buttonTitle = config.buttonTitle || this.translate.instant('BUTTON.OK');

    if (Capacitor.isNativePlatform()) {
      return await Dialog.alert(config);
    }

    const alert = await this.alertController.create({
      buttons: [config.buttonTitle],
      header: config.title,
      message: config.message,
    });

    await alert.present();
    await alert.onWillDismiss();

    return Promise.resolve();
  }

  public async showConfirm(config: ConfirmOptions): Promise<boolean> {
    config.okButtonTitle = config.okButtonTitle || this.translate.instant('BUTTON.OK');
    config.cancelButtonTitle = config.cancelButtonTitle || this.translate.instant('BUTTON.CANCEL');

    if (Capacitor.isNativePlatform()) {
      const result: ConfirmResult = await Dialog.confirm(config);

      return Promise.resolve(result.value);
    }

    let confirmed: boolean = false;
    const alert = await this.alertController.create({
      backdropDismiss: false,
      buttons: [
        {
          role: 'cancel',
          text: config.cancelButtonTitle,
        },
        {
          handler: (): void => {
            confirmed = true;
          },
          role: 'confirm',
          text: config.okButtonTitle,
        },
      ],
      header: config.title,
      message: config.message,
    });

    await alert.present();
    await alert.onWillDismiss();

    return Promise.resolve(confirmed);
  }

  // eslint-disable-next-line max-lines-per-function
  public async showPrompt(config: PromptOptions): Promise<string> {
    config.okButtonTitle = config.okButtonTitle || this.translate.instant('BUTTON.OK');
    config.cancelButtonTitle = config.cancelButtonTitle || this.translate.instant('BUTTON.CANCEL');

    if (Capacitor.isNativePlatform()) {
      const promptResult: PromptResult = await Dialog.prompt(config);

      return Promise.resolve(promptResult.cancelled
        ? ''
        : promptResult.value);
    }

    const inputName = 'promptInput';

    let confirmed: boolean = false;
    const alert = await this.alertController.create({
      backdropDismiss: false,
      buttons: [
        {
          role: 'cancel',
          text: config.cancelButtonTitle,
        },
        {
          handler: (): void => {
            confirmed = true;
          },
          text: config.okButtonTitle,
        },
      ],
      cssClass: 'prompt-alert',
      header: config.title,
      inputs: [
        {
          name: inputName,
          placeholder: config.inputPlaceholder,
          type: 'text',
          value: config.inputText,
        },
      ],
      message: config.message,
    });

    await alert.present();
    const input: HTMLElement = document.querySelector('ion-alert input');

    if (input) {
      input.focus();
    }

    const result = await alert.onWillDismiss();

    return Promise.resolve(confirmed
      ? result.data.values[inputName]
      : '');
  }
}
